import React, { useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Container,
  Grid,
  IconButton,
  Snackbar,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ArrowRight, AssignmentReturn, LocalOffer } from '@material-ui/icons';

import moment from 'moment';
import clsx from 'clsx';
import Header from '../components/Header';
import Section from '../components/Section';
import Banner from '../components/Banner';
import BlockViewer from '../components/BlockViewer';
import OutlinedButton from '../components/OutlinedButton';
import PostList from '../components/PostList';
import Widget from '../components/Widget';
import PreFooter from '../components/PreFooter';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';

import {
  fetchPosts,
  fetchPost,
  fetchOtherPosts,
  convertDocsToPosts,
} from '../services/posts';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles<Theme>((theme) => ({
  fbSocial: {
    height: 20,
  },
  image: {
    height: 300,
    objectFit: 'cover',
    width: '100%',
  },
  tagIcon: {
    marginRight: theme.spacing(1),
  },
}));
interface IParams {
  type: postTypes;
  id: string;
}
interface INewestPosts {
  events: Post[];
  articles: Post[];
  videos: Post[];
}

const initialVideoState = { open: false, url: '' };
const newestEventCount = 4;
const newestArticleCount = 4;
const newestVideoCount = 3;

const SinglePost = () => {
  const history = useHistory();
  const { state, pathname } = useLocation();
  const [initLoading, setInitLoading] = useState(true);
  const { fbSocial, image, tagIcon } = useStyles();
  const { type, id }: IParams = useParams();
  const [post, setPost] = useState<Post | undefined>();
  const [newestPosts, setNewestPosts] = useState<INewestPosts>();
  const [otherPosts, setOtherPosts] = useState<Post[]>();
  const [videoState, setVideoState] = useState<IVideoState>(initialVideoState);
  const [copyClipboard, setCopyClipboard] = useState<boolean>(false);
  useEffect(() => {
    const dataExist = state !== undefined;
    if (!dataExist) {
      const fetchData = async () => {
        const value = await fetchPost(type, id);
        setPost(value);
      };
      fetchData();
    } else {
      setPost(state as Post);
    }
    const fetchOthers = async () => {
      const otherPosts = await fetchOtherPosts(id, type, 4);
      setOtherPosts(otherPosts);
    };
    fetchOthers();
  }, [state, type, id]);
  useEffect(() => {
    const fetchData = async () => {
      const [eventData, articleData, videoData] = await Promise.all([
        fetchPosts('event', newestEventCount),
        fetchPosts('article', newestArticleCount),
        fetchPosts('video', newestVideoCount),
      ]);
      setNewestPosts({
        events: convertDocsToPosts(eventData.docs),
        articles: convertDocsToPosts(articleData.docs),
        videos: convertDocsToPosts(videoData.docs),
      });
      setInitLoading(false);
    };
    fetchData();
    (window as any).FB?.XFBML.parse();
  }, []);
  const handleCopyClipboard = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopyClipboard(true);
  };

  const handleClickHome = () => {
    history.push('/');
  };
  const renderArticle = () => {
    return (
      <>
        <VideoPlayer
          onClose={() => {
            setVideoState((state) => ({ ...state, open: false }));
          }}
          open={videoState.open}
          url={videoState.url}
        />
        <img
          className={image}
          alt="post-img"
          src={
            post?.imageUrl !== '' ? post?.imageUrl : '/images/placeholder.png'
          }
        />
        <Box height={16} />
        {post && (
          <Box display="flex" flexDirection="column">
            <Typography variant="h5">{post.title}</Typography>
            <Typography variant="caption">
              {moment(post.date).format('LLL')}
            </Typography>
            <BlockViewer content={JSON.parse(post.content)} />
          </Box>
        )}
        {post?.tags && post?.tags?.length > 0 && (
          <Box display="flex" alignItems="center">
            <LocalOffer fontSize="small" className={tagIcon} />
            <Typography variant="subtitle1">
              {post?.tags?.map((tag, i) => (!i ? tag : `, ${tag}`))}
            </Typography>
          </Box>
        )}
        <Box
          bgcolor="#454057"
          mt={4}
          px={2}
          py={1}
          borderRadius={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>SHARE :</Box>
          <Box display="flex" alignItems="center">
            <div
              className={clsx('fb-like', fbSocial)}
              data-href={pathname}
              data-width=""
              data-layout="button"
              data-action="like"
              data-size="small"
              data-share="true"
            />
            <IconButton
              color="inherit"
              onClick={handleCopyClipboard}
              size="small"
            >
              <AssignmentReturn />
            </IconButton>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" mt={4}>
          <OutlinedButton onClick={handleClickHome}>
            กลับหน้าหลัก
          </OutlinedButton>
        </Box>
      </>
    );
  };

  const renderSideBar = () => {
    return (
      <>
        <Banner onButtonClick={() => history.push('/game')} />
        <Box height={16} />
        {newestPosts && (
          <>
            <Widget title="กิจกรรมใหม่ล่าสุด">
              <PostList
                posts={newestPosts.events}
                column={1}
                imageHeight={80}
                sideBySide
                hideDetail
                dateOnly
                titleSize="body2"
                type="event"
                loading={initLoading}
              />
            </Widget>
            <Box height={16} />
            <Widget title="บทความใหม่ล่าสุด">
              <PostList
                posts={newestPosts.articles}
                column={1}
                imageHeight={80}
                sideBySide
                hideDetail
                titleSize="body2"
                type="article"
                loading={initLoading}
              />
            </Widget>
            <Box height={16} />
            <Widget title="วิดีโอใหม่ล่าสุด">
              <PostList
                posts={newestPosts.videos}
                column={1}
                hideDetail
                titleSize="body2"
                icon={
                  <Avatar style={{ backgroundColor: '#0003' }}>
                    <ArrowRight style={{ fontSize: 48, color: '#FFF' }} />
                  </Avatar>
                }
                type="video"
                onClickVideo={(url) => {
                  setVideoState((state) => ({
                    ...state,
                    open: true,
                    url,
                  }));
                }}
                loading={initLoading}
              />
            </Widget>
          </>
        )}
      </>
    );
  };

  const renderBottomBar = () => {
    return (
      <>
        <Box height={16} />
        <Typography variant="h6">บทความอื่น ๆ</Typography>
        <Box height={8} />
        <PostList
          posts={otherPosts ?? []}
          column={4}
          hideDetail
          type={type}
          loading={false}
        />
      </>
    );
  };

  return (
    <>
      <Header />
      <Container maxWidth="md">
        <Section>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              {renderArticle()}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderSideBar()}
            </Grid>
          </Grid>
          {renderBottomBar()}
        </Section>
        <Snackbar
          open={copyClipboard}
          autoHideDuration={6000}
          onClose={() => setCopyClipboard(false)}
        >
          <Alert severity="success">link copied to clipboard!</Alert>
        </Snackbar>
      </Container>
      <PreFooter />
      <Footer />
    </>
  );
};

export default SinglePost;
